import { apiClient } from '../config/api';
import { ENDPOINTS, PaginatedResponse, QueryParams } from '../config/endpoints';
import { BaseService } from './baseService';
import { Cotacao } from '../types/cotacao';
import { Cliente, Produto } from '../types/entities';

export interface Situacao {
  id: string;
  nome: string;
}

export interface CalculoData {
  id?: string;
  calculo?: string;
  cpfCnpj?: string;
  // Dados do cliente agora em objeto separado
  cliente?: Cliente;
  // Campos antigos mantidos para compatibilidade
  nomeCliente?: string;
  email?: string;
  celular?: string;
  dddCelular?: string;
  tipoPessoa?: string;
  // Fim dos campos de cliente
  produto?: string | Produto;
  situacao?: string | Situacao;
  situacaoId?: string;
  perdido?: boolean;
  zerokm?: boolean;
  valor?: number | string;
  percentualComis?: number | string;
  valorComis?: number | string;
  inicioVigencia?: string;
  dataInclusa?: string;
  item?: string;
  usuarioInclusao?: string;
  nivel?: string;
  divisao?: string;
  empresaId?: string;
  criadoEm?: string;
  atualizadoEm?: string;
  // Additional fields from Calculo interface
  status?: string;
  cotacaoCia?: string;
  seguradora?: string;
  informacoes?: string;
  cotado?: boolean;
  aprovado?: boolean;
  retornar?: boolean;
  dataRetorno?: string;
  tipoOrcamento?: string;
  dataCancelamento?: string;
  motivo?: string;
  apoliceAnterior?: string;
  campanha?: string;
  aprovadoOutro?: boolean;
  codProspect?: string;
  cotacoes?: Cotacao[];
}

// Funções auxiliares para acessar dados do cliente com fallback
export const getClienteNome = (calculo: CalculoData): string => {
  return calculo.cliente?.nome || calculo.nomeCliente || '-';
};

export const getClienteEmail = (calculo: CalculoData): string => {
  return calculo.cliente?.email || calculo.email || '-';
};

export const getClienteTelefone = (calculo: CalculoData): string => {
  if (calculo.cliente?.telefone) {
    return calculo.cliente.telefone;
  }
  if (calculo.dddCelular && calculo.celular) {
    return `(${calculo.dddCelular}) ${calculo.celular}`;
  }
  return '-';
};

export const getClienteCpfCnpj = (calculo: CalculoData): string => {
  return calculo.cliente?.cpfCnpj || calculo.cpfCnpj || '-';
};

export const getClienteTipoPessoa = (calculo: CalculoData): string => {
  return calculo.cliente?.tipoPessoa || calculo.tipoPessoa || '-';
};

export const getProdutoNome = (calculo: CalculoData): string => {
  if (typeof calculo.produto === 'string') {
    return calculo.produto;
  }
  if (calculo.produto && typeof calculo.produto === 'object' && 'nome' in calculo.produto) {
    return (calculo.produto as Produto).nome;
  }
  return '-';
};

export const getSituacaoNome = (calculo: CalculoData): string => {
  if (typeof calculo.situacao === 'string') {
    return calculo.situacao;
  }
  if (calculo.situacao && typeof calculo.situacao === 'object' && 'nome' in calculo.situacao) {
    return (calculo.situacao as Situacao).nome;
  }
  return '-';
};

export interface CalculoListParams extends QueryParams {
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  status?: string;
  produto?: string;
  seguradora?: string;
  situacaoId?: string;
  cpfCnpj?: string;
}

class CalculoService extends BaseService<CalculoData> {
  protected endpoints = ENDPOINTS.CALCULO;

  // Override list to accept CalculoListParams
  async list(params?: CalculoListParams): Promise<PaginatedResponse<CalculoData>> {
    return super.list(params);
  }
}

export const calculoService = new CalculoService();
